import React, { useState } from 'react';
import { FaEnvelope, FaUser, FaCheck, FaTimes, FaChevronDown } from 'react-icons/fa';
import PageHeader from '../components/layout/PageHeader';
import RichText from '../components/ui/RichText';
import pageContent from '../cms/page-content.json';
import {
  getMailingListFormDef,
  getMailingListField,
  getMailingListOptionLabel,
} from '../utils/mailingListFormMeta';
import '../css/pages/MailingList.css';

// Calculate dynamic graduation years based on current date
const getGraduationYears = () => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // 1-12
  
  // June 1 cutoff: who is considered a "senior" right now?
  // Jan-May: Current year graduates this spring (they're seniors)
  // Jun-Dec: Current year already graduated, next year are seniors
  const seniorGradYear = currentMonth >= 6 ? currentYear + 1 : currentYear;
  
  // Calculate other class years explicitly
  const juniorGradYear = seniorGradYear + 1;
  const sophomoreGradYear = seniorGradYear + 2;
  const freshmanGradYear = seniorGradYear + 3;
  
  return {
    senior: seniorGradYear,
    junior: juniorGradYear,
    sophomore: sophomoreGradYear,
    freshman: freshmanGradYear
  };
};

const MailingList = () => {
  const mailingForm = getMailingListFormDef();
  const firstNameField = getMailingListField('firstName');
  const lastNameField = getMailingListField('lastName');
  const emailField = getMailingListField('email');
  const memberTypeField = getMailingListField('memberType');
  const graduationYearField = getMailingListField('graduationYear');
  const familyMemberTypeField = getMailingListField('familyMemberType');

  // Calculate graduation years once (fallback if CMS options are empty)
  const graduationYears = getGraduationYears();
  const graduationYearOptions =
    graduationYearField.options?.length
      ? graduationYearField.options
      : [
          String(graduationYears.senior),
          String(graduationYears.junior),
          String(graduationYears.sophomore),
          String(graduationYears.freshman),
          'not-sure',
        ];
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    memberType: '',
    graduationYear: '',
    familyMemberType: ''
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validation, setValidation] = useState({
    firstName: null,
    lastName: null,
    email: null,
    memberType: null,
    graduationYear: null,
    familyMemberType: null
  });

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateField = (name, value) => {
    if (!value.trim()) return false;
    if (name === 'email') return validateEmail(value);
    return true;
  };

  // Helper function to check if graduation year is required
  const isGraduationYearRequired = () => {
    return formData.memberType === 'undergraduate-student';
  };

  // Helper function to check if family member type is required
  const isFamilyMemberTypeRequired = () => {
    return formData.memberType === 'family-member';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      // Clear dependent fields when member type changes
      if (name === 'memberType') {
        newData.graduationYear = '';
        newData.familyMemberType = '';
      }
      
      return newData;
    });
    
    // Clear validation when user starts typing
    if (validation[name] !== null) {
      setValidation(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const newValidation = {
      firstName: validateField('firstName', formData.firstName),
      lastName: validateField('lastName', formData.lastName),
      email: validateField('email', formData.email),
      memberType: validateField('memberType', formData.memberType),
      graduationYear: isGraduationYearRequired() ? validateField('graduationYear', formData.graduationYear) : true,
      familyMemberType: isFamilyMemberTypeRequired() ? validateField('familyMemberType', formData.familyMemberType) : true
    };
    
    setValidation(newValidation);
    
    // Check if all validations pass
    if (Object.values(newValidation).every(Boolean)) {
      setIsSubmitting(true);
      
      try {
        // Make actual API call to Constant Contact
        const response = await fetch('/api/constant-contact/subscribe', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: formData.email,
            first_name: formData.firstName,
            last_name: formData.lastName,
            member_type: formData.memberType,
            graduation_year: formData.graduationYear,
            family_member_type: formData.familyMemberType
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        
        if (result.success) {
          setSuccessMessage(result.message);
          setSubmitted(true);
        } else {
          throw new Error(result.message || 'Subscription failed');
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        alert('Sorry, there was an error subscribing you to our mailing list. Please contact Rina (rnewhou2@jh.edu) to be manually added.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const isFormValid = formData.firstName.trim().length > 0 && 
                     formData.lastName.trim().length > 0 && 
                     formData.email.trim().length > 0 && 
                     validateEmail(formData.email) &&
                     formData.memberType.trim().length > 0 &&
                     (!isGraduationYearRequired() || formData.graduationYear.trim().length > 0) &&
                     (!isFamilyMemberTypeRequired() || formData.familyMemberType.trim().length > 0);

  const resetForm = () => {
    setSubmitted(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      memberType: '',
      graduationYear: '',
      familyMemberType: ''
    });
    setValidation({
      firstName: null,
      lastName: null,
      email: null,
      memberType: null,
      graduationYear: null,
      familyMemberType: null
    });
  };

  if (submitted) {
    return (
      <div>
        <PageHeader 
          title={pageContent.mlSuccessTitle} 
          subtitle={pageContent.mlSuccessSubtitle}
        />
        <div className="mailing-list-container">
          <div className="form-container">
            <div className="success-message">
              <FaCheck className="success-icon" />
              {successMessage || pageContent.mlSuccessMessage}
            </div>
            <button onClick={resetForm} className="add-another-button">
              Add Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader 
        title={pageContent.mlTitle} 
        subtitle={pageContent.mlSubtitle}
      />
      <div className="mailing-list-container">
        <div className="form-container">
          <RichText
            value={mailingForm.note || pageContent.mlDescription}
            className="form-description"
          />
          
          <form className="mailing-list-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="firstName" className="form-label">{firstNameField.label || 'First Name'} *</label>
              <div className="input-container">
                <div className={`input-icon ${validation.firstName !== null ? (validation.firstName ? 'valid' : 'invalid') : ''}`}>
                  <FaUser />
                </div>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={`form-input ${validation.firstName !== null ? (validation.firstName ? 'valid' : 'invalid') : ''}`}
                  placeholder={firstNameField.placeholder || 'Enter your first name'}
                  required
                />
                {validation.firstName !== null && (
                  <div className={`validation-icon ${validation.firstName ? 'valid' : 'invalid'}`}>
                    {validation.firstName ? <FaCheck /> : <FaTimes />}
                  </div>
                )}
              </div>
              {validation.firstName === false && (
                <div className="error-message">
                  <FaTimes />
                  First name is required
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="lastName" className="form-label">{lastNameField.label || 'Last Name'} *</label>
              <div className="input-container">
                <div className={`input-icon ${validation.lastName !== null ? (validation.lastName ? 'valid' : 'invalid') : ''}`}>
                  <FaUser />
                </div>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`form-input ${validation.lastName !== null ? (validation.lastName ? 'valid' : 'invalid') : ''}`}
                  placeholder={lastNameField.placeholder || 'Enter your last name'}
                  required
                />
                {validation.lastName !== null && (
                  <div className={`validation-icon ${validation.lastName ? 'valid' : 'invalid'}`}>
                    {validation.lastName ? <FaCheck /> : <FaTimes />}
                  </div>
                )}
              </div>
              {validation.lastName === false && (
                <div className="error-message">
                  <FaTimes />
                  Last name is required
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">{emailField.label || 'Email Address'} *</label>
              <div className="input-container">
                <div className={`input-icon ${validation.email !== null ? (validation.email ? 'valid' : 'invalid') : ''}`}>
                  <FaEnvelope />
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`form-input ${validation.email !== null ? (validation.email ? 'valid' : 'invalid') : ''}`}
                  placeholder={emailField.placeholder || 'Enter your email address'}
                  required
                />
                {validation.email !== null && (
                  <div className={`validation-icon ${validation.email ? 'valid' : 'invalid'}`}>
                    {validation.email ? <FaCheck /> : <FaTimes />}
                  </div>
                )}
              </div>
              {validation.email === false && (
                <div className="error-message">
                  <FaTimes />
                  Please enter a valid email address
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="memberType" className="form-label">{memberTypeField.label || 'Community Member Type'} *</label>
              <div className="input-container">
                <div className={`input-icon ${validation.memberType !== null ? (validation.memberType ? 'valid' : 'invalid') : ''}`}>
                  <FaUser />
                </div>
                <select
                  id="memberType"
                  name="memberType"
                  value={formData.memberType}
                  onChange={handleInputChange}
                  className={`form-input form-select ${validation.memberType !== null ? (validation.memberType ? 'valid' : 'invalid') : ''}`}
                  required
                >
                  <option value="">{memberTypeField.placeholder || 'Select your role'}</option>
                  {(memberTypeField.options || []).map((option) => (
                    <option key={option} value={option}>
                      {getMailingListOptionLabel(option)}
                    </option>
                  ))}
                </select>
                <div className="select-arrow">
                  <FaChevronDown />
                </div>
                {validation.memberType !== null && (
                  <div className={`validation-icon ${validation.memberType ? 'valid' : 'invalid'}`}>
                    {validation.memberType ? <FaCheck /> : <FaTimes />}
                  </div>
                )}
              </div>
              {validation.memberType === false && (
                <div className="error-message">
                  <FaTimes />
                  Please select your community member type
                </div>
              )}
            </div>

            {isGraduationYearRequired() && (
              <div className="form-group form-group-conditional">
                <label htmlFor="graduationYear" className="form-label">{graduationYearField.label || 'Graduation Year'} *</label>
                <div className="input-container">
                  <div className={`input-icon ${validation.graduationYear !== null ? (validation.graduationYear ? 'valid' : 'invalid') : ''}`}>
                    <FaUser />
                  </div>
                  <select
                    id="graduationYear"
                    name="graduationYear"
                    value={formData.graduationYear}
                    onChange={handleInputChange}
                    className={`form-input form-select ${validation.graduationYear !== null ? (validation.graduationYear ? 'valid' : 'invalid') : ''}`}
                    required
                  >
                    <option value="">{graduationYearField.placeholder || 'Select your graduation year'}</option>
                    {graduationYearOptions.map((option) => (
                      <option key={option} value={option}>
                        {getMailingListOptionLabel(option)}
                      </option>
                    ))}
                  </select>
                  <div className="select-arrow">
                    <FaChevronDown />
                  </div>
                  {validation.graduationYear !== null && (
                    <div className={`validation-icon ${validation.graduationYear ? 'valid' : 'invalid'}`}>
                      {validation.graduationYear ? <FaCheck /> : <FaTimes />}
                    </div>
                  )}
                </div>
                {validation.graduationYear === false && (
                  <div className="error-message">
                    <FaTimes />
                    Please select your graduation year
                  </div>
                )}
              </div>
            )}

            {isFamilyMemberTypeRequired() && (
              <div className="form-group form-group-conditional">
                <label htmlFor="familyMemberType" className="form-label">{familyMemberTypeField.label || 'Family Member Type'} *</label>
                <div className="input-container">
                  <div className={`input-icon ${validation.familyMemberType !== null ? (validation.familyMemberType ? 'valid' : 'invalid') : ''}`}>
                    <FaUser />
                  </div>
                  <select
                    id="familyMemberType"
                    name="familyMemberType"
                    value={formData.familyMemberType}
                    onChange={handleInputChange}
                    className={`form-input form-select ${validation.familyMemberType !== null ? (validation.familyMemberType ? 'valid' : 'invalid') : ''}`}
                    required
                  >
                    <option value="">{familyMemberTypeField.placeholder || 'Select relationship'}</option>
                    {(familyMemberTypeField.options || []).map((option) => (
                      <option key={option} value={option}>
                        {getMailingListOptionLabel(option)}
                      </option>
                    ))}
                  </select>
                  <div className="select-arrow">
                    <FaChevronDown />
                  </div>
                  {validation.familyMemberType !== null && (
                    <div className={`validation-icon ${validation.familyMemberType ? 'valid' : 'invalid'}`}>
                      {validation.familyMemberType ? <FaCheck /> : <FaTimes />}
                    </div>
                  )}
                </div>
                {validation.familyMemberType === false && (
                  <div className="error-message">
                    <FaTimes />
                    Please select your relationship
                  </div>
                )}
              </div>
            )}

            <button type="submit" className="submit-button" disabled={!isFormValid || isSubmitting}>
              {isSubmitting ? 'Subscribing...' : mailingForm.submitText || 'Subscribe to Mailing List'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MailingList; 